import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { 
  Package, 
  Plus, 
  Edit2, 
  Trash2, 
  Search,
  DollarSign,
  MoreVertical,
  ChevronDown,
  ChevronRight,
  Globe,
  MapPin,
  Save,
  X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';

const PartTypeManagement = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingPartType, setEditingPartType] = useState(null);
  const [expandedPartTypes, setExpandedPartTypes] = useState(new Set());
  const [editingPricing, setEditingPricing] = useState(null); // {partTypeId, region, price}
  const [newPricing, setNewPricing] = useState({ region: '', price: '' });
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    average_price_cents: ''
  });

  // Fetch part types with regional pricing
  const { data: partTypes = [], isLoading } = useQuery({
    queryKey: ['partTypes', searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('include_pricing', 'true');
      const response = await apiClient.get(`/api/competitive-intelligence/part-types?${params}`);
      return response.data;
    }
  });

  // Fetch available regions
  const { data: regionsData } = useQuery({
    queryKey: ['availableRegions'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/available-regions');
      return response.data;
    }
  });

  const availableRegions = regionsData?.regions || [];

  // Create part type mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await apiClient.post('/api/competitive-intelligence/part-types', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['partTypes']);
      setIsCreateDialogOpen(false);
      resetForm();
    }
  });

  // Update part type mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`/api/competitive-intelligence/part-types/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['partTypes']);
      setEditingPartType(null);
      resetForm();
    }
  });

  // Delete part type mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`/api/competitive-intelligence/part-types/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['partTypes']);
    }
  });

  // Add regional pricing mutation
  const addPricingMutation = useMutation({
    mutationFn: async ({ partTypeId, data }) => {
      const response = await apiClient.post(
        `/api/competitive-intelligence/part-types/${partTypeId}/regional-pricing`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['partTypes']);
      setNewPricing({ region: '', price: '' });
    }
  });

  // Update regional pricing mutation
  const updatePricingMutation = useMutation({
    mutationFn: async ({ partTypeId, pricingId, data }) => {
      const response = await apiClient.put(
        `/api/competitive-intelligence/part-types/${partTypeId}/regional-pricing/${pricingId}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['partTypes']);
      setEditingPricing(null);
    }
  });

  // Delete regional pricing mutation
  const deletePricingMutation = useMutation({
    mutationFn: async ({ partTypeId, pricingId }) => {
      const response = await apiClient.delete(
        `/api/competitive-intelligence/part-types/${partTypeId}/regional-pricing/${pricingId}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['partTypes']);
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      average_price_cents: ''
    });
  };

  const handleEdit = (partType) => {
    setEditingPartType(partType);
    setFormData({
      name: partType.name || '',
      code: partType.code || '',
      description: partType.description || '',
      average_price_cents: partType.average_price_cents ? (partType.average_price_cents / 100).toString() : ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      average_price_cents: formData.average_price_cents ? Math.round(parseFloat(formData.average_price_cents) * 100) : 0
    };

    if (editingPartType) {
      updateMutation.mutate({ id: editingPartType.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingPartType(null);
    resetForm();
  };

  const toggleExpand = (partTypeId) => {
    setExpandedPartTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(partTypeId)) {
        newSet.delete(partTypeId);
      } else {
        newSet.add(partTypeId);
      }
      return newSet;
    });
  };

  const handleAddPricing = (partTypeId) => {
    if (!newPricing.region || !newPricing.price) return;
    addPricingMutation.mutate({
      partTypeId,
      data: {
        region: newPricing.region,
        price_cents: Math.round(parseFloat(newPricing.price) * 100),
        currency: 'USD'
      }
    });
  };

  const handleUpdatePricing = (partTypeId, pricingId, newPrice) => {
    updatePricingMutation.mutate({
      partTypeId,
      pricingId,
      data: { price_cents: Math.round(parseFloat(newPrice) * 100) }
    });
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  // Get regions not yet assigned to a part type
  const getAvailableRegionsForPartType = (partType) => {
    const usedRegions = new Set(partType.regional_pricing?.map(p => p.region) || []);
    return availableRegions.filter(r => !usedRegions.has(r));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Part Types</h2>
          <p className="text-sm text-slate-500 mt-1">
            Define your business segments and regional pricing for market analysis
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Part Type
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search part types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Part Types List */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-500">Loading part types...</div>
      ) : partTypes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No part types defined</h3>
            <p className="text-slate-500 mb-4">Define your business segments to categorize market share</p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Part Type
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {partTypes.map((partType) => {
            const isExpanded = expandedPartTypes.has(partType.id);
            const regionalPricing = partType.regional_pricing || [];
            const availableForPricing = getAvailableRegionsForPartType(partType);
            
            return (
              <Card key={partType.id} className="overflow-hidden">
                <Collapsible open={isExpanded} onOpenChange={() => toggleExpand(partType.id)}>
                  <div className="flex items-center justify-between p-4 hover:bg-slate-50">
                    <CollapsibleTrigger className="flex items-center gap-3 flex-1">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Package className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">{partType.name}</span>
                          {partType.code && (
                            <Badge variant="outline" className="font-mono text-xs">
                              {partType.code}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5">
                          {partType.description || 'No description'}
                        </p>
                      </div>
                    </CollapsibleTrigger>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-700">
                            Global: {formatCurrency(partType.average_price_cents)}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {regionalPricing.length} regional price{regionalPricing.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm">
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-slate-400" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(partType)}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit Part Type
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => deleteMutation.mutate(partType.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  <CollapsibleContent>
                    <div className="border-t bg-slate-50/50 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          Regional Pricing
                        </h4>
                        <p className="text-xs text-slate-500">
                          Prices by manufacturing/sales region
                        </p>
                      </div>
                      
                      {/* Regional Pricing Table */}
                      <div className="bg-white rounded-lg border overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50 border-b">
                              <th className="text-left p-3 font-medium text-slate-600">Region</th>
                              <th className="text-right p-3 font-medium text-slate-600">Price</th>
                              <th className="text-right p-3 font-medium text-slate-600">vs Global</th>
                              <th className="w-20 p-3"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Global Price Row */}
                            <tr className="border-b bg-blue-50/30">
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <Globe className="h-4 w-4 text-blue-600" />
                                  <span className="font-medium text-blue-900">Global Default</span>
                                </div>
                              </td>
                              <td className="text-right p-3 font-semibold text-blue-900">
                                {formatCurrency(partType.average_price_cents)}
                              </td>
                              <td className="text-right p-3 text-blue-600">-</td>
                              <td className="p-3"></td>
                            </tr>
                            
                            {/* Regional Prices */}
                            {regionalPricing.map((pricing) => {
                              const diff = pricing.price_cents - partType.average_price_cents;
                              const diffPercent = ((diff / partType.average_price_cents) * 100).toFixed(0);
                              const isEditing = editingPricing?.id === pricing.id;
                              
                              return (
                                <tr key={pricing.id} className="border-b hover:bg-slate-50">
                                  <td className="p-3">
                                    <div className="flex items-center gap-2">
                                      <MapPin className="h-4 w-4 text-slate-400" />
                                      <span className="text-slate-700">{pricing.region}</span>
                                    </div>
                                    {pricing.notes && (
                                      <p className="text-xs text-slate-400 mt-0.5 ml-6">{pricing.notes}</p>
                                    )}
                                  </td>
                                  <td className="text-right p-3">
                                    {isEditing ? (
                                      <Input
                                        type="number"
                                        step="0.01"
                                        className="w-24 h-8 text-right"
                                        defaultValue={(pricing.price_cents / 100).toFixed(2)}
                                        onBlur={(e) => {
                                          if (e.target.value) {
                                            handleUpdatePricing(partType.id, pricing.id, e.target.value);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && e.target.value) {
                                            handleUpdatePricing(partType.id, pricing.id, e.target.value);
                                          } else if (e.key === 'Escape') {
                                            setEditingPricing(null);
                                          }
                                        }}
                                        autoFocus
                                      />
                                    ) : (
                                      <span className="font-medium text-slate-900">
                                        {formatCurrency(pricing.price_cents)}
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-right p-3">
                                    <span className={`text-sm ${
                                      diff > 0 ? 'text-red-600' : diff < 0 ? 'text-green-600' : 'text-slate-500'
                                    }`}>
                                      {diff > 0 ? '+' : ''}{diffPercent}%
                                    </span>
                                  </td>
                                  <td className="p-3">
                                    <div className="flex items-center justify-end gap-1">
                                      {!isEditing && (
                                        <>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0"
                                            onClick={() => setEditingPricing(pricing)}
                                          >
                                            <Edit2 className="h-3.5 w-3.5" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                                            onClick={() => deletePricingMutation.mutate({
                                              partTypeId: partType.id,
                                              pricingId: pricing.id
                                            })}
                                          >
                                            <Trash2 className="h-3.5 w-3.5" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            
                            {/* Add New Regional Price Row */}
                            {availableForPricing.length > 0 && (
                              <tr className="bg-slate-50/50">
                                <td className="p-3">
                                  <Select
                                    value={newPricing.region}
                                    onValueChange={(value) => setNewPricing(prev => ({ ...prev, region: value }))}
                                  >
                                    <SelectTrigger className="w-48 h-8">
                                      <SelectValue placeholder="Select region..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {availableForPricing.map(region => (
                                        <SelectItem key={region} value={region}>
                                          {region}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="p-3 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <span className="text-slate-400">$</span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      className="w-24 h-8 text-right"
                                      placeholder="0.00"
                                      value={newPricing.price}
                                      onChange={(e) => setNewPricing(prev => ({ ...prev, price: e.target.value }))}
                                    />
                                  </div>
                                </td>
                                <td className="p-3"></td>
                                <td className="p-3">
                                  <Button
                                    size="sm"
                                    className="h-7"
                                    disabled={!newPricing.region || !newPricing.price || addPricingMutation.isPending}
                                    onClick={() => handleAddPricing(partType.id)}
                                  >
                                    <Plus className="h-3.5 w-3.5 mr-1" />
                                    Add
                                  </Button>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        
                        {regionalPricing.length === 0 && (
                          <div className="p-6 text-center border-t">
                            <MapPin className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">No regional pricing defined</p>
                            <p className="text-xs text-slate-400 mt-1">Global price will be used for all regions</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen || !!editingPartType} onOpenChange={(open) => {
        if (!open) handleCloseDialog();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingPartType ? 'Edit Part Type' : 'Add Part Type'}
            </DialogTitle>
            <DialogDescription>
              {editingPartType 
                ? 'Update part type information'
                : 'Define a new business segment for market analysis'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Part Type Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Airbags"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., AIR"
                  maxLength={10}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this part type..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="average_price_cents" className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-slate-400" />
                Global Default Price ($)
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="average_price_cents"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.average_price_cents}
                  onChange={(e) => setFormData(prev => ({ ...prev, average_price_cents: e.target.value }))}
                  placeholder="85.00"
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-slate-500">
                Used as fallback when regional pricing is not defined. You can add regional prices after creating.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingPartType ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartTypeManagement;
